import React from "react";
import {
  domToReact,
  type Element,
  type DOMNode,
  type HTMLReactParserOptions,
  type Text,
} from "html-react-parser";
import { hasClass, htmlAttribsToReactProps, isTag } from "./htmlParsingUtils";
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import type { WorkspaceReferenceVisibilityContext } from "@/utils/documentLinkWorkspaceVersionCollapse";
import { MultiLinkAnchor } from "../components/MultiLinkAnchor";
import { ParsedDocumentLinkAnchor } from "../components/ParsedDocumentLinkAnchor";
import {
  coerceInjectionPlan,
  hasVisibleDocumentLinkText,
  type DocumentLinkInjectionPlan,
} from "./documentLinkInjectionPlan";

interface CreateHtmlParserOptionsParams {
  extractDocumentLinkKeys: (attrs: { id?: string | null }) => string[];
  resolveDocumentLink: (
    keys: string[]
  ) => { linkId: string; link: DocumentLink } | null;
  navigateToDocumentLink: (
    linkId: string,
    event?: React.MouseEvent | MouseEvent
  ) => void;
  buildDocumentLinkHref?: (link: DocumentLink) => string | undefined;
  /** When set, resolves whether to use origin* or destination* fields for the open document */
  currentDocumentId?: string;
  /** Precomputed merged/split reference injections (same HTML as parse input). */
  injectionPlan: DocumentLinkInjectionPlan;
  /** All links for the open document (used to hide stale workspace refs in anchors). */
  allDocumentLinks?: DocumentLink[];
  /** Precomputed once per document parse — avoids O(anchors × links) rescans. */
  workspaceVisibilityContext?: WorkspaceReferenceVisibilityContext;
  /** Id of the container currently under amendment review, if any. */
  activeAmendmentContainerId?: string;
  /**
   * Pre-sanitized diff markup for the amendment-review container. When present,
   * that container renders this as React-owned innerHTML instead of its parsed
   * children, so React never reconciles nodes mutated behind its back. See
   * LAR-306.
   */
  activeAmendmentDiffHtml?: string | null;
}

// Void HTML elements that cannot have children
const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

/**
 * Page wrapper of a converted PDF (`.ocr-anchor-wrapper`): let the browser skip
 * style, layout and paint for pages outside the viewport — ~20k OCR text lines
 * otherwise cost ~0.5 s per full relayout (LAR-487). `useWarmSkippedPages`
 * lays the skipped pages out in idle time afterwards, so whatever needs them all
 * at once later (find-in-page, the browser's text capture) finds it done.
 *
 * A skipped page must not size itself from its content, so it takes the page
 * raster's intrinsic aspect ratio. The converter's own `aspect-ratio …
 * !important` never applies: React drops `!important` values. Pages without a
 * usable raster size keep rendering normally rather than collapse to 0 px.
 */
function ocrPageWrapperStyle(el: Element): React.CSSProperties | undefined {
  if (!hasClass(el, "ocr-anchor-wrapper")) return undefined;
  const img = (el.children as DOMNode[]).find(
    (child): child is Element => isTag(child) && child.name === "img"
  );
  const width = Number(img?.attribs.width);
  const height = Number(img?.attribs.height);
  if (!(width > 0 && height > 0)) return undefined;
  return { aspectRatio: `${width} / ${height}`, contentVisibility: "auto" };
}

/** Count text nodes in an html-react-parser subtree (same order as replace traversal). */
function countParserTextNodes(nodes: readonly DOMNode[]): number {
  let count = 0;
  for (const node of nodes) {
    if (node.type === "text") {
      count += 1;
    } else if (node.type === "tag") {
      const child = node as Element;
      if (VOID_ELEMENTS.has(child.name.toLowerCase())) {
        continue;
      }
      count += countParserTextNodes(child.children as DOMNode[]);
    }
  }
  return count;
}

export function createHtmlParserOptions({
  extractDocumentLinkKeys,
  resolveDocumentLink,
  navigateToDocumentLink,
  buildDocumentLinkHref,
  currentDocumentId,
  injectionPlan,
  allDocumentLinks,
  workspaceVisibilityContext,
  activeAmendmentContainerId,
  activeAmendmentDiffHtml,
}: CreateHtmlParserOptionsParams): HTMLReactParserOptions {
  const plan = coerceInjectionPlan(injectionPlan as unknown);

  let textNodeIndex = 0;

  const elementStack: Array<{ isATag: boolean }> = [];

  const options: HTMLReactParserOptions = {
    replace(node) {
      if (node.type === "tag") {
        const el = node as Element;
        const isATag = el.name === "a";

        elementStack.push({ isATag });

        // Amendment-review container: render the diff as React-owned innerHTML
        // so React (not imperative DOM mutation) owns these nodes. Prevents the
        // removeChild crash on consolidation switch (LAR-306).
        if (
          activeAmendmentDiffHtml != null &&
          el.attribs?.id &&
          el.attribs.id === activeAmendmentContainerId &&
          !VOID_ELEMENTS.has(el.name.toLowerCase())
        ) {
          // Keep textNodeIndex aligned with the precomputed injectionPlan even
          // though we skip parsing this container's children (LAR-306).
          textNodeIndex += countParserTextNodes(el.children as DOMNode[]);
          const result = React.createElement(el.name, {
            ...htmlAttribsToReactProps(el.attribs),
            dangerouslySetInnerHTML: { __html: activeAmendmentDiffHtml },
          });
          elementStack.pop();
          return result;
        }

        if (isATag) {
          const elementId = el.attribs?.id;
          const baseProps = htmlAttribsToReactProps(el.attribs);
          const anchorProps: React.ComponentPropsWithoutRef<"a"> & {
            [key: string]: unknown;
          } = {
            ...baseProps,
            rel: "noopener noreferrer nofollow",
          } as React.ComponentPropsWithoutRef<"a"> & { [key: string]: unknown };

          const linkKeys = extractDocumentLinkKeys({
            id: elementId,
          });
          const resolved = resolveDocumentLink(linkKeys);
          const linkId = resolved?.linkId;
          const link = resolved?.link;
          const hasPotentialDocLink = linkKeys.length > 0;

          const result = React.createElement(ParsedDocumentLinkAnchor, {
            allDocumentLinks,
            workspaceVisibilityContext,
            link,
            linkId,
            anchorProps,
            navigateToDocumentLink,
            documentLinkHref: link
              ? buildDocumentLinkHref?.(link)
              : undefined,
            hasPotentialDocLink,
            currentDocumentId,
            children: domToReact(el.children as DOMNode[], options),
          });

          elementStack.pop();
          return result;
        }

        const isVoidElement = VOID_ELEMENTS.has(el.name.toLowerCase());

        if (isVoidElement) {
          const result = React.createElement(
            el.name,
            htmlAttribsToReactProps(el.attribs)
          );
          elementStack.pop();
          return result;
        }

        const processedChildren = domToReact(el.children as DOMNode[], options);
        const props = htmlAttribsToReactProps(el.attribs);
        const pageStyle = ocrPageWrapperStyle(el);
        if (pageStyle) {
          props.style = {
            ...(props.style as React.CSSProperties),
            ...pageStyle,
          };
        }
        const result = React.createElement(el.name, props, processedChildren);
        elementStack.pop();
        return result;
      } else if (node.type === "text") {
        const textNode = node as Text;
        const text = textNode.data || "";

        const idx = textNodeIndex++;

        const isInsideATag = elementStack.some((el) => el.isATag);
        if (isInsideATag) {
          return text;
        }

        const fragments = plan.get(idx);
        if (!fragments || fragments.length === 0) {
          return text;
        }

        const sorted = [...fragments]
          .filter((f) => {
            if (f.start >= f.end) return false;
            const sub = text.substring(f.start, f.end);
            if (sub !== f.matchText) return false;
            return hasVisibleDocumentLinkText(sub);
          })
          .sort((a, b) => a.start - b.start);
        if (sorted.length === 0) {
          return text;
        }

        const parts: React.ReactElement[] = [];
        let cursor = 0;

        for (let i = 0; i < sorted.length; i++) {
          const frag = sorted[i];
          if (frag.start > cursor) {
            const beforeText = text.substring(cursor, frag.start);
            if (beforeText) {
              parts.push(
                React.createElement(
                  React.Fragment,
                  { key: `t-${idx}-${cursor}` },
                  beforeText
                )
              );
            }
          }

          const uniqueKey = `mla-${idx}-${frag.triggerId}-${i}`;
          parts.push(
            React.createElement(MultiLinkAnchor, {
              key: uniqueKey,
              allDocumentLinks,
              workspaceVisibilityContext,
              links: frag.links,
              matchText: frag.matchText,
              currentDocumentId,
              navigateToDocumentLink,
              triggerId: frag.triggerId,
            })
          );

          cursor = frag.end;
        }

        if (cursor < text.length) {
          const rest = text.substring(cursor);
          if (rest) {
            parts.push(
              React.createElement(
                React.Fragment,
                { key: `t-${idx}-end` },
                rest
              )
            );
          }
        }

        if (parts.length === 0) {
          return text;
        }

        return <React.Fragment>{parts}</React.Fragment>;
      }
      return undefined;
    },
  };

  return options;
}
